import { gql } from '@apollo/client'
import {
  TableCellBasic,
  TableCellSize,
  TableCellSkeleton,
} from '@npco/zeller-design-system'
import { CellContext } from '@tanstack/react-table'

import { useCardStatus } from 'hooks/useCardStatus'

import { useExtendedColumnConfig } from '../../hooks/useExtendedColumnConfig/useExtendedColumnConfig'
import { CardTransactionsMissingDetailsCountCellDebitCardV2Fragment } from './CardTransactionsMissingDetailsCountCell.generated'
import { CardTransactionsMissingDetailsCountCellContent } from './CardTransactionsMissingDetailsCountCellContent'

export const CardTransactionsMissingDetailsCountCell = <T,>({
  cell,
  getValue,
  row,
  table,
  column,
}: CellContext<
  T,
  CardTransactionsMissingDetailsCountCellDebitCardV2Fragment
>) => {
  const { skeletonLoaderWidth, cellSize } = useExtendedColumnConfig<T>({
    row,
    column,
  })

  const isLoading = table.options.meta?.isLoading
  const data = isLoading
    ? undefined
    : getValue<CardTransactionsMissingDetailsCountCellDebitCardV2Fragment>()
  const { isClosed, isInactive } = useCardStatus(data)

  return (
    <TableCellBasic
      className="card-transactions-missing-details-count-cell"
      dataTestId={cell.id}
      forwardedProps={{ style: { padding: 0 } }}
      isLoading={isLoading}
      justifyContent="center"
      key={cell.id}
      size={TableCellSize.MEDIUM}
      cellTextSize="Small"
      cellSize={cellSize}
    >
      {isLoading && (
        <TableCellSkeleton
          width={skeletonLoaderWidth}
          size={TableCellSize.SMALL}
        />
      )}
      {!isLoading && !isClosed && !isInactive && (
        <CardTransactionsMissingDetailsCountCellContent
          card={data}
          index={row.index}
        />
      )}
    </TableCellBasic>
  )
}

CardTransactionsMissingDetailsCountCell.fragments = {
  DebitCardV2: gql`
    fragment CardTransactionsMissingDetailsCountCellDebitCardV2Fragment on DebitCardV2 {
      ...UseCardStatusDebitCardV2Fragment
      ...CardTransactionsMissingDetailsCountCellContentDebitCardV2Fragment
    }

    ${useCardStatus.fragments.DebitCardV2}
    ${CardTransactionsMissingDetailsCountCellContent.fragments.DebitCardV2}
  `,
}
